import React from 'react';

const MONTHS_TO_SHOW = 6;

const toDate = (value) => {
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

const formatCurrency = (value) =>
  `\u20B9${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const monthLabel = (date) =>
  date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

const pctChange = (current, previous) => {
  if (previous > 0) return ((current - previous) / previous) * 100;
  if (current > 0) return 100;
  return 0;
};

const downloadTextFile = (name, content) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ReportsPage = ({ orders = [], products = [], users = [] }) => {
  const now = React.useMemo(() => new Date(), []);
  const orderRows = React.useMemo(
    () =>
      (orders || [])
        .map((o) => ({
          ...o,
          _date: toDate(o.created_at),
          _total: Number(o.total_amount || 0),
          _status: String(o.status || 'PLACED').toUpperCase(),
        }))
        .filter((o) => o._date),
    [orders]
  );

  const monthDates = React.useMemo(() => {
    const list = [];
    for (let i = MONTHS_TO_SHOW - 1; i >= 0; i -= 1) {
      list.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    }
    return list;
  }, [now]);

  const monthMap = React.useMemo(() => {
    const map = {};
    monthDates.forEach((d) => {
      map[monthKey(d)] = { key: monthKey(d), label: monthLabel(d), revenue: 0, orders: 0 };
    });
    orderRows.forEach((o) => {
      const key = monthKey(o._date);
      if (!map[key]) return;
      map[key].revenue += o._total;
      map[key].orders += 1;
    });
    return map;
  }, [monthDates, orderRows]);

  const monthlyRows = React.useMemo(
    () => monthDates.map((d) => monthMap[monthKey(d)]).filter(Boolean),
    [monthDates, monthMap]
  );

  const latest = monthlyRows[monthlyRows.length - 1] || { revenue: 0, orders: 0 };
  const previous = monthlyRows[monthlyRows.length - 2] || { revenue: 0, orders: 0 };
  const maxRevenue = Math.max(1, ...monthlyRows.map((m) => m.revenue));

  const categoryMix = React.useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      const c = String(p.category || 'Uncategorized').trim() || 'Uncategorized';
      map.set(c, (map.get(c) || 0) + 1);
    });
    const rows = Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    const total = rows.reduce((sum, r) => sum + r.count, 0);
    return { rows: rows.slice(0, 5), total };
  }, [products]);

  const statusCounts = React.useMemo(() => {
    const counts = { PLACED: 0, PACKED: 0, SHIPPED: 0, DELIVERED: 0, CANCELLED: 0 };
    orderRows.forEach((o) => {
      if (counts[o._status] !== undefined) counts[o._status] += 1;
    });
    return counts;
  }, [orderRows]);

  const userApproval = React.useMemo(() => {
    const total = (users || []).length;
    const approved = (users || []).filter((u) => Boolean(u.is_approved)).length;
    return { total, approved, pending: Math.max(0, total - approved) };
  }, [users]);

  const topProducts = React.useMemo(
    () =>
      [...(products || [])]
        .sort((a, b) => Number(a.quantity || 0) - Number(b.quantity || 0))
        .slice(0, 6),
    [products]
  );

  const handleDownload = () => {
    const lines = [
      'Month,Orders,Revenue',
      ...monthlyRows.map((m) => `${m.label},${m.orders},${m.revenue}`),
      '',
      'Status,Count',
      ...Object.entries(statusCounts).map(([k, v]) => `${k},${v}`),
      '',
      'Category,Products',
      ...categoryMix.rows.map((r) => `${r.name},${r.count}`),
      '',
      'User Segment,Count',
      `Approved,${userApproval.approved}`,
      `Pending,${userApproval.pending}`,
      `Total,${userApproval.total}`,
    ];
    downloadTextFile(`reports-${monthKey(now)}.csv`, lines.join('\n'));
  };

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3>Reports & Analytics</h3>
        <button className="secondary" onClick={handleDownload}>Download CSV</button>
      </div>
      <div className="split">
        <div className="card chart-card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h4>Monthly Revenue ({MONTHS_TO_SHOW}M)</h4>
            <span className="muted">{formatCurrency(latest.revenue)}</span>
          </div>
          <div className="chart-line">
            {monthlyRows.map((m, i) => (
              <div
                key={`line-${i}`}
                title={`${m.label}: ${formatCurrency(m.revenue)}`}
                style={{ height: `${Math.max(8, (m.revenue / maxRevenue) * 100)}%` }}
              />
            ))}
          </div>
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
            {monthlyRows.map((m) => (
              <span key={`ml-${m.key}`} className="muted">{m.label}</span>
            ))}
          </div>
        </div>
        <div className="card chart-card">
          <h4>Category Mix</h4>
          <div className="status-mix-list">
            {categoryMix.rows.map((row) => {
              const pct = categoryMix.total ? (row.count / categoryMix.total) * 100 : 0;
              return (
                <div key={row.name} className="status-mix-row">
                  <div className="row">
                    <span className="badge-soft">{row.name}</span>
                    <span className="muted">{row.count}</span>
                  </div>
                  <div className="status-mix-track">
                    <div className="status-mix-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="muted">{pct.toFixed(1)}%</div>
                </div>
              );
            })}
            {!categoryMix.rows.length ? <div className="muted">No categories available.</div> : null}
          </div>
        </div>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>This Month</th>
            <th>Last Month</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Orders</td>
            <td>{latest.orders}</td>
            <td>{previous.orders}</td>
            <td>{`${pctChange(latest.orders, previous.orders).toFixed(1)}%`}</td>
          </tr>
          <tr>
            <td>Revenue</td>
            <td>{formatCurrency(latest.revenue)}</td>
            <td>{formatCurrency(previous.revenue)}</td>
            <td>{`${pctChange(latest.revenue, previous.revenue).toFixed(1)}%`}</td>
          </tr>
          <tr>
            <td>Delivered Orders</td>
            <td>{statusCounts.DELIVERED}</td>
            <td>{statusCounts.SHIPPED}</td>
            <td>{statusCounts.SHIPPED ? `${((statusCounts.DELIVERED / statusCounts.SHIPPED) * 100).toFixed(1)}% conv` : '0%'}</td>
          </tr>
          <tr>
            <td>User Approvals</td>
            <td>{userApproval.approved}</td>
            <td>{userApproval.pending}</td>
            <td>{userApproval.total ? `${((userApproval.approved / userApproval.total) * 100).toFixed(1)}% approved` : '0%'}</td>
          </tr>
        </tbody>
      </table>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h4>Inventory Risk Report</h4>
          <span className="muted">Lowest stock first</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Brand</th>
              <th>Category</th>
              <th>Stock</th>
            </tr>
          </thead>
          <tbody>
            {topProducts.map((p) => (
              <tr key={p._id || p.name}>
                <td>{p.name || 'Product'}</td>
                <td>{p.brand || '-'}</td>
                <td>{p.category || '-'}</td>
                <td>{Number(p.quantity || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!topProducts.length ? <div className="muted" style={{ marginTop: 8 }}>No products available.</div> : null}
      </div>
    </div>
  );
};

export default ReportsPage;
